import React, { memo, useCallback } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { bodyPortal } from '../../../../App';
import { useTranslation } from 'react-i18next';
import OptionsOverlay from './OptionsOverlay';

const VALUES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

interface IProps {
  isVisible: boolean;
  rate?: number;
  onClose: () => void;
  onSelect: (rate: number) => void;
}
const PlaybackRateOverlay = ({ isVisible, rate = 1, onSelect, onClose }: IProps) => {
  const { t } = useTranslation();

  const handleSelect = useCallback(
    (newPlaybackRate: number) => {
      onClose?.();

      if (rate === newPlaybackRate) {
        return;
      }

      onSelect(newPlaybackRate);
    },
    [onClose, onSelect, rate],
  );

  const Overlay = (
    <OptionsOverlay isVisible={isVisible} onClose={onClose}>
      <S.TitleContainer>
        <S.Title>{t('components.translations.translateSubtitles')}</S.Title>
      </S.TitleContainer>

      {VALUES.map((value) => (
        <Row value={value} isSelected={value === rate} key={value} onSelect={handleSelect} />
      ))}
    </OptionsOverlay>
  );

  return createPortal(Overlay, bodyPortal.current);
};
export default memo(PlaybackRateOverlay);

const Row = ({
  value,
  isSelected,
  onSelect,
}: {
  value: number;
  isSelected: boolean;
  onSelect: (value: number) => void;
}) => {
  const handleSelect = useCallback(() => {
    onSelect(value);
  }, [onSelect, value]);

  return (
    <S.Row onClick={handleSelect} key={value}>
      <S.RowLabel>{value}x</S.RowLabel>
      {isSelected && (
        <>
          <S.Grow />
          <S.Dot />
        </>
      )}
    </S.Row>
  );
};

const S = {
  TitleContainer: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    padding-right: 16px;
  `,
  Title: styled.div`
    display: flex;
    align-items: center;
    padding: 14px 24px;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.white};
    font-weight: 700;
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
  `,
  ActiveButton: styled.div<{ isActive }>`
    display: flex;
    align-items: center;
    padding: 4px 8px;
    font-size: 10px;
    color: ${({ theme }) => theme.colors.white};
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    border: 1px solid ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    opacity: ${({ isActive }) => (isActive ? 0.7 : 0.5)};
    cursor: pointer;
    transition: opacity 150ms ease-in-out;

    &:hover {
      opacity: 0.7;
    }
  `,
  SubtitlesDisabled: styled.div`
    display: flex;
    align-items: center;
    padding: 8px 24px 20px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.lightGray2};
  `,
  SearchContainer: styled.div`
    position: relative;
    padding: 8px;
  `,
  SearchInput: styled.input`
    padding: 4px 16px;
    width: 100%;
    color: ${({ theme }) => theme.colors.lightGray2};
    font-size: 14px;
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    line-height: 24px;
    border: 1px solid ${({ theme, value }) => (value ? theme.colors.darkGray9 : theme.colors.darkGray11)};
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.darkGray11};
    outline: none;
    transition: border-color 150ms ease-in-out;

    &::placeholder {
      color: ${({ theme }) => theme.colors.darkGray7};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.darkGray9};
    }
  `,
  CloseContainer: styled.div<{
    hasValue?: boolean;
  }>`
    position: absolute;
    top: 8px;
    right: 8px;
    bottom: 8px;
    display: flex;
    align-items: center;
    padding: 1px 10px 0px 8px;
    cursor: pointer;
    opacity: ${({ hasValue }) => (hasValue ? 1 : 0)};
    transition: opacity 150ms ease-in-out;
  `,
  LoadingContainer: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
  `,
  VoiceoversLabel: styled.div`
    padding: 0 24px 8px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.lightGray2};
  `,
  LangsContainer: styled.div`
    flex: 1;
    overflow: auto;
  `,
  Row: styled.div<{ isRtl?: boolean }>`
    display: flex;
    flex-direction: ${({ isRtl }) => (isRtl ? 'row-reverse' : 'row')};
    align-items: center;
    padding: 0 24px;
    height: 42px;
    cursor: pointer;

    &:hover {
      background-color: ${({ theme }) => theme.colors.darkGray9};
    }
  `,
  RowLabel: styled.div`
    display: flex;
    align-items: center;
    height: 44px;
    cursor: pointer;
  `,
  Grow: styled.div`
    flex-grow: 1;
  `,
  Separator: styled.div<{
    marginTop?: boolean;
    marginBottom?: boolean;
  }>`
    ${({ marginTop }) => (marginTop ? 'margin-top: 8px;' : '')}
    ${({ marginBottom }) => (marginBottom ? 'margin-bottom: 8px;' : '')}
    height: 1px;
    background-color: ${({ theme }) => theme.colors.darkGray11};
  `,
  IconContainer: styled.div<{ marginLeft?: boolean; marginRight?: boolean }>`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    ${({ marginLeft }) => (marginLeft ? 'margin-left: 8px;' : '')}
    ${({ marginRight }) => (marginRight ? 'margin-right: 8px;' : '')}
    cursor: pointer;
  `,
  Dot: styled.div`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.white};
  `,
  Error: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    color: ${({ theme }) => theme.colors.white};
  `,
  LinkBtn: styled.span`
    text-decoration: underline;
    cursor: pointer;
  `,
  VerifiedText: styled.div`
    margin: 1px 8px 0;
    color: ${({ theme }) => theme.colors.green2};
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
  `,
};
